import { ENV } from "@/ENV";
import {
  FMFolderType,
  useAllSiteNamesQuery,
  useFMFileDeleteMutation,
  useFMFileUpdateMutation,
  useFMFolderDeleteMutation,
  useFMFolderFavoriteCreateMutation,
  useFMFolderFavoriteDeleteMutation,
  useFMFolderUpdateMutation,
} from "@/graphql";
import { computed, ref, type ComputedRef } from "vue";
import { useTranslation } from "@/language/translation";
import { FOLDER_MIME_TYPE, type FolderTableContent } from "./FMFilePreviewHelper";
import { confirmModal } from "@/components/shared/form/ConfirmationModal";
import { useToast } from "primevue/usetoast";
import type { DocumentNode } from "graphql";
import type { ApolloCache } from "@apollo/client/cache";

export function useFMFolderNames() {
  const { result, loading } = useAllSiteNamesQuery({ fetchPolicy: "cache-first" });

  const siteMap = computed(
    () => new Map(result.value?.Sites?.map((site) => [site.id.toString(), site.name]) ?? []),
  );

  const ready = computed(() => !loading.value && result.value?.Sites != null);

  const { t } = useTranslation();

  function getFolderStats(folder: {
    siteId?: number;
    name: string;
    type: FMFolderType;
    path: string;
  }) {
    let name = folder.name;
    let deletableAndMoveable = false;
    let colorClass: string | undefined = undefined;
    let icon = "fas fa-folder";

    switch (folder.type) {
      case FMFolderType.DEFAULT:
        deletableAndMoveable = true;
        break;
      case FMFolderType.ROOT:
        name = ENV.VITE_APP_CUSTOMER_NAME;
        colorClass = "text-primary";
        icon = "fas fa-database";
        break;
      case FMFolderType.SITE_ROOT:
        colorClass = "text-primary";
        name = loading.value
          ? folder.name
          : (siteMap.value.get(folder.siteId?.toString() ?? "-1") ?? t("global.deleted"));
        icon = "fas fa-industry";

        break;
      case FMFolderType.RESTRICTED:
        break;
      case FMFolderType.SQDC:
        icon = "fas fa-th";
        name = t("sidebar.sqdc.title");
        break;
      case FMFolderType.SQDC_EVENT_ACTION:
        name = t("modules.filemanagement.view.FMFileExplorer.eventAction");
        icon = "fas fa-th";
        break;
      case FMFolderType.SQDC_TEMPLATE:
        name = t("sidebar.sqdc.children.sqdcTemplates.children.boardTemplates.title");
        break;
      case FMFolderType.SQDC_TILES:
        name = t("modules.filemanagement.view.FMFileExplorer.tiles");
        break;
      case FMFolderType.TEMPORARY:
        name = t("modules.filemanagement.view.FMFileExplorer.temporal");
        icon = "fas fa-dumpster";
        break;
      case FMFolderType.AUDIT:
        name = t("modules.filemanagement.view.FMFileExplorer.audit");
        icon = "fas fa-eye";
        break;
      case FMFolderType.LOST_AND_FOUND:
        name = t("modules.filemanagement.view.FMFileExplorer.lostAndFound");
        icon = "fas fa-hand-holding-droplet";
        break;
      case FMFolderType.SYSTEM:
        colorClass = "text-info";
        icon = "fas fa-display";
        name = t("modules.filemanagement.view.FMFileExplorer.system");
        break;
      case FMFolderType.AVATAR:
        icon = "fas fa-circle-user";
        name = t("modules.filemanagement.view.FMFileExplorer.avatar");
        break;
      case FMFolderType.BDE:
        icon = "fas fa-soap";
        name = t("sidebar.bde.title");
        break;
      case FMFolderType.NEWS:
        icon = "far fa-newspaper";
        name = t("sidebar.news.title");
        break;
      case FMFolderType.NEWS_SLIDER:
        name = t("sidebar.news.children.slider.title");
        break;
      case FMFolderType.QUALITY:
        icon = "fas fa-yin-yang";
        name = t("sidebar.quality.title");
        break;
      case FMFolderType.DYNAMIC_FORM:
        icon = "fas fa-file-pen";
        name = t("sidebar.bde.children.settings.children.DFDynamicForm.title");
        break;
    }

    return {
      ready,
      name,
      deletableAndMoveable,
      colorClass: colorClass ?? `h-color-y400`,
      icon,
    };
  }

  return {
    ready: computed(() => true),
    loading,
    getFolderStats,
  };
}

export function getFolderOrFileTypeColor(mimeType?: string) {
  if (mimeType == null) {
    return "";
  }

  const parts = mimeType.split("/");

  switch (parts[0]) {
    case "application": {
      switch (parts[1]) {
        case "pdf":
          return "var(--p-red-400)";
        case "json":
          return "var(--p-yellow-300)";
        default:
          return "var(--p-gray-400)";
      }
    }
    case "video":
      return "var(--p-blue-400)";
    case "image":
      return "var(--p-green-400)";
    case "audio":
      return "var(--p-purple-400)";
    case "text":
      return "var(--p-indigo-400)";
    case FOLDER_MIME_TYPE:
      return "var(--p-yellow-400)";
    default:
      return "var(--p-gray-400)";
  }
}

export function useFileOrFolderOperations(
  props: ComputedRef<{
    refetchAwait:
      | {
          query: DocumentNode;
          variables: any;
        }[]
      | undefined;
    markFovorites: number[] | undefined;
  }>,
) {
  const toast = useToast();
  const { t } = useTranslation();

  const { mutate: mutateFileDelete, loading: loadingFileDelete } = useFMFileDeleteMutation();
  const { mutate: mutateFolderDelete, loading: loadingFolderDelete } = useFMFolderDeleteMutation();

  function evictDeletedFileOrFolder(
    cache: ApolloCache<unknown>,
    typename: "FMFile" | "FMFolder",
    id: number,
  ) {
    cache.evict({
      id: cache.identify({
        __typename: typename,
        id,
      }),
    });
    cache.gc();
  }

  async function deleteFileOrFolder(fileOrFolder: FolderTableContent) {
    const successDeletePromise = new Promise<boolean>((resolve) => {
      confirmModal({
        showDeleteText: true,
        message: t("modules.filemanagement.view.FMFileExplorer.confirmDelete"),
        accept: async () => {
          if (fileOrFolder.mimeType === FOLDER_MIME_TYPE) {
            if (fileOrFolder.folderId != null) {
              await mutateFolderDelete(
                {
                  id: fileOrFolder.folderId,
                },
                {
                  refetchQueries: props.value.refetchAwait,
                  awaitRefetchQueries: true,
                  update(cache) {
                    evictDeletedFileOrFolder(cache, "FMFolder", fileOrFolder.folderId!);
                  },
                },
              );
              toast.add({
                life: 5000,
                summary: t("global.successful"),
                detail: t("global.success"),
                severity: "success",
              });
            }
          } else {
            if (fileOrFolder.fileId != null) {
              await mutateFileDelete(
                {
                  ids: [fileOrFolder.fileId],
                },
                {
                  refetchQueries: props.value.refetchAwait,
                  awaitRefetchQueries: true,
                  update(cache) {
                    evictDeletedFileOrFolder(cache, "FMFile", fileOrFolder.fileId!);
                  },
                },
              );
              toast.add({
                life: 5000,
                summary: t("global.successful"),
                detail: t("global.success"),
                severity: "success",
              });
            }
          }
          resolve(true);
        },
        reject: () => resolve(false),
      });
    });
    return successDeletePromise;
  }

  function isFileOrFolderDisabled(fileOrFolder: FolderTableContent) {
    if (fileOrFolder.mimeType !== FOLDER_MIME_TYPE) {
      return false;
    }
    return fileOrFolder.folderType !== FMFolderType.DEFAULT;
  }

  const { mutate: mutateFolder, loading: loadingFolderUpdate } = useFMFolderUpdateMutation();
  const { mutate: mutateFile, loading: loadingFileUpdate } = useFMFileUpdateMutation();

  const fileFolderNameEditString = ref<{ [key: string]: string | undefined }>({});

  async function moveFileOrFolderToFolder(
    fileOrFolder: FolderTableContent,
    folderId: number | number[] | undefined,
  ) {
    if (fileOrFolder.mimeType === FOLDER_MIME_TYPE) {
      if (fileOrFolder.folderId != null && folderId != null && typeof folderId === "number") {
        const result = await mutateFolder(
          {
            id: fileOrFolder.folderId,
            folder: {
              parentId: folderId,
            },
          },
          {
            refetchQueries: props.value.refetchAwait,
            awaitRefetchQueries: true,
          },
        );
        toast.add({
          life: 5000,
          summary: t("global.successful"),
          detail: t("global.success"),
          severity: "success",
        });
        return result?.data?.FMFolderUpdate;
      }
    } else {
      if (fileOrFolder.fileId != null && folderId != null && typeof folderId === "number") {
        await mutateFile(
          {
            id: fileOrFolder.fileId,
            file: {
              folderId: folderId,
            },
          },
          {
            refetchQueries: props.value.refetchAwait,
            awaitRefetchQueries: true,
          },
        );
        toast.add({
          life: 5000,
          summary: t("global.successful"),
          detail: t("global.success"),
          severity: "success",
        });
      }
    }
  }

  async function updateNameOfFileOrFolder(element?: FolderTableContent, newName?: string) {
    if (element != null) {
      if (element.mimeType === FOLDER_MIME_TYPE) {
        if (element.folderId != null) {
          await mutateFolder(
            {
              id: element.folderId,
              folder: {
                name: newName,
              },
            },
            {
              refetchQueries: props.value.refetchAwait,
              awaitRefetchQueries: true,
            },
          );
          toast.add({
            life: 5000,
            summary: t("global.successful"),
            detail: t("global.success"),
            severity: "success",
          });
        }
      } else {
        if (element.fileId != null) {
          await mutateFile(
            {
              id: element.fileId,
              file: {
                name: newName,
              },
            },
            {
              refetchQueries: props.value.refetchAwait,
              awaitRefetchQueries: true,
            },
          );
          toast.add({
            life: 5000,
            summary: t("global.successful"),
            detail: t("global.success"),
            severity: "success",
          });
        }
      }
    }
  }

  const { mutate: favoriteCreate, loading: loadingFavoriteCreate } =
    useFMFolderFavoriteCreateMutation();
  const { mutate: favoriteDelete, loading: loadingFavoriteDelete } =
    useFMFolderFavoriteDeleteMutation();

  async function toggleFavorite(data: FolderTableContent) {
    if (data.folderId == null) {
      return;
    }
    if (props.value.markFovorites == null) {
      throw Error("markFovorites prop is required for toggleFavorite operation");
    }
    if (props.value.markFovorites?.includes(data.folderId)) {
      await favoriteDelete(
        { folderIds: [data.folderId] },
        {
          update(cache, { data: dataParam }) {
            const folders = dataParam?.FMFolderFavoriteDelete ?? [];
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                FMFolderFavorites(_existing, { toReference }) {
                  return folders
                    .map((folder) =>
                      toReference({
                        __typename: folder.__typename,
                        id: folder.id,
                      }),
                    )
                    .filter(Boolean);
                },
              },
            });
          },
        },
      );
      toast.add({
        life: 5000,
        summary: t("global.successful"),
        detail: t("global.success"),
        severity: "success",
      });
    } else {
      await favoriteCreate(
        { folderIds: [data.folderId] },
        {
          update(cache, { data: dataParam }) {
            const folders = dataParam?.FMFolderFavoriteCreate ?? [];
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                FMFolderFavorites(_existing, { toReference }) {
                  return folders
                    .map((folder) =>
                      toReference({
                        __typename: folder.__typename,
                        id: folder.id,
                      }),
                    )
                    .filter(Boolean);
                },
              },
            });
          },
        },
      );
      toast.add({
        life: 5000,
        summary: t("global.successful"),
        detail: t("global.success"),
        severity: "success",
      });
    }
  }

  const loading = computed(
    () =>
      loadingFileDelete.value ||
      loadingFolderDelete.value ||
      loadingFolderUpdate.value ||
      loadingFileUpdate.value ||
      loadingFavoriteCreate.value ||
      loadingFavoriteDelete.value,
  );

  return {
    deleteFileOrFolder,
    isFileOrFolderDisabled,
    moveFileOrFolderToFolder,
    updateNameOfFileOrFolder,
    toggleFavorite,
    fileFolderNameEditString,
    loading,
  };
}

export type FMFolderTableCreatedFolder = {
  id: number;
  name: string;
  path: string;
  isLeafFolder: boolean;
  siteId?: number;
  index?: number;
  type: FMFolderType;
  parentId: number;
};

export type FMFolderTableMovedFolder = FMFolderTableCreatedFolder & {
  previousParentId?: number;
};
